import { useEffect, useState } from 'react';
import {
  BarChart2,
  Clock,
  AlertTriangle,
  TrendingUp,
  MessageSquare,
  CheckCircle2,
} from 'lucide-react';
import { apiFetch } from '../../utils/api';

function formatTime(value, unit) {
  if (unit === 'minutes') return `${value} min`;
  return `${value}h`;
}

function resolutionToMs(value, unit) {
  if (unit === 'hours') return value * 60 * 60 * 1000;
  return value * 60 * 1000;
}

export default function ReportsPanel({ conversations = [], slaConfig = {} }) {
  const [metrics, setMetrics] = useState(null);
  const [metricsError, setMetricsError] = useState(false);

  useEffect(() => {
    let active = true;
    apiFetch('/reports/metrics')
      .then((data) => {
        if (active) {
          setMetrics(data);
          setMetricsError(false);
        }
      })
      .catch(() => {
        if (active) setMetricsError(true);
      });
    return () => { active = false; };
  }, []);

  const firstResp = slaConfig.firstResponseTime || { value: 15, unit: 'minutes' };
  const resolution = slaConfig.resolutionTime || { value: 4, unit: 'hours' };
  const alerts = slaConfig.delayAlerts || [];

  const totalConversations = metrics != null ? metrics.totalConversations : conversations.length;
  const resolvedCount = metrics != null ? metrics.resolvedCount : conversations.filter((c) => c.stage === 'closed').length;
  const avgFirstResponse = metrics?.avgFirstResponseMinutes ?? (metricsError ? null : undefined);
  const avgResolution = metrics?.avgResolutionHours ?? (metricsError ? null : undefined);
  let overdueCount = metrics?.overdueCount;
  let warningCount = metrics?.warningCount;
  if (overdueCount == null || warningCount == null) {
    const resolutionMs = resolutionToMs(resolution.value, resolution.unit);
    const warningPercent = (alerts.find((a) => a.percent < 100 && a.enabled !== false)?.percent) ?? 80;
    const now = Date.now();
    overdueCount = 0;
    warningCount = 0;
    for (const c of conversations) {
      if (c.stage === 'closed') continue;
      const created = c.created_at ? new Date(c.created_at).getTime() : null;
      if (created == null) continue;
      const ageMs = now - created;
      if (ageMs >= resolutionMs) overdueCount++;
      else if (ageMs >= (resolutionMs * warningPercent) / 100) warningCount++;
    }
  }

  return (
    <div className="flex-1 flex flex-col bg-appPage overflow-hidden">
      <div className="px-8 pt-6 pb-4 shrink-0 border-b border-appSurfaceBorder bg-appSurface">
        <h1 className="text-2xl font-bold text-appText tracking-tight">Relatórios</h1>
        <p className="text-sm text-appTextMuted mt-1">
          Métricas de SLA, tempo de resposta e alertas de atraso
        </p>
      </div>

      <div className="flex-1 overflow-y-auto p-8">
        <div className="max-w-6xl mx-auto space-y-8">
          {/* Cards de resumo */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <SummaryCard
              icon={MessageSquare}
              label="Conversas ativas"
              value={totalConversations}
              subtext="em andamento"
            />
            <SummaryCard
              icon={CheckCircle2}
              label="Resolvidas"
              value={resolvedCount}
              subtext={`de ${totalConversations || 1}`}
            />
            <SummaryCard
              icon={Clock}
              label="1ª resposta (média)"
              value={typeof avgFirstResponse === 'number' ? `${Math.round(avgFirstResponse)} min` : '—'}
              subtext={`Meta: ${formatTime(firstResp.value, firstResp.unit)}`}
            />
            <SummaryCard
              icon={TrendingUp}
              label="Resolução (média)"
              value={typeof avgResolution === 'number' ? `${avgResolution.toFixed(1)}h` : '—'}
              subtext={`Meta: ${formatTime(resolution.value, resolution.unit)}`}
            />
          </div>

          {/* SLA & Metas */}
          <div className="bg-appSurface rounded-2xl border border-appSurfaceBorder p-6">
            <h2 className="text-lg font-bold text-appText mb-4 flex items-center gap-2">
              <Clock size={20} className="text-appPrimary" />
              Metas de SLA
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm font-medium text-appTextMuted mb-1">Tempo de primeira resposta</p>
                <p className="text-2xl font-bold text-appText">
                  {formatTime(firstResp.value, firstResp.unit)}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-appTextMuted mb-1">Tempo de resolução</p>
                <p className="text-2xl font-bold text-appText">
                  {formatTime(resolution.value, resolution.unit)}
                </p>
              </div>
            </div>
          </div>

          {/* Alertas de atraso */}
          <div className="bg-appSurface rounded-2xl border border-appSurfaceBorder p-6">
            <h2 className="text-lg font-bold text-appText mb-4 flex items-center gap-2">
              <AlertTriangle size={20} className="text-appPrimary" />
              Alertas de atraso
            </h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-xl bg-rose-50 border border-rose-200">
                <div>
                  <p className="font-bold text-rose-800">Alerta Crítico</p>
                  <p className="text-sm text-rose-600">
                    {overdueCount} conversa(s) acima do tempo de resolução
                  </p>
                </div>
                <span className="text-2xl font-bold text-rose-700">{overdueCount}</span>
              </div>
              <div className="flex items-center justify-between p-4 rounded-xl bg-amber-50 border border-amber-200">
                <div>
                  <p className="font-bold text-amber-800">Alerta de Atenção</p>
                  <p className="text-sm text-amber-600">
                    {warningCount} conversa(s) próximas do limite
                  </p>
                </div>
                <span className="text-2xl font-bold text-amber-700">{warningCount}</span>
              </div>
              <div className="text-sm text-appTextMuted">
                Configuração: {alerts.filter((a) => a.enabled).map((a) => `${a.label} em ${a.percent}%`).join(' • ') || 'Nenhum alerta configurado'}
              </div>
            </div>
          </div>

          {/* Gráfico placeholder */}
          <div className="bg-appSurface rounded-2xl border border-appSurfaceBorder p-6">
            <h2 className="text-lg font-bold text-appText mb-4 flex items-center gap-2">
              <BarChart2 size={20} className="text-appPrimary" />
              Desempenho por período
            </h2>
            <div className="h-64 flex items-center justify-center bg-appPage/50 rounded-xl border-2 border-dashed border-appSurfaceBorder">
              <div className="text-center">
                <BarChart2 size={48} className="mx-auto text-appTextMuted mb-2" />
                <p className="text-appTextMuted font-medium">Gráfico em breve</p>
                <p className="text-xs text-appTextMuted mt-1">
                  Os dados serão exibidos aqui quando houver histórico
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, subtext }) {
  return (
    <div className="bg-appSurface rounded-2xl border border-appSurfaceBorder p-5 shadow-sm">
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 rounded-xl bg-appPrimaryLight text-appPrimaryText">
          <Icon size={20} />
        </div>
        <span className="text-xs font-bold text-appTextMuted uppercase">{label}</span>
      </div>
      <p className="text-2xl font-bold text-appText">{value}</p>
      <p className="text-xs text-appTextMuted mt-1">{subtext}</p>
    </div>
  );
}
